import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, TextField, Button, Typography, Alert,
  InputAdornment, IconButton, CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, Business } from "@mui/icons-material";
import { gqlPublic, ADMIN_SIGN_IN } from "../../api/graphql";
import { useAuth } from "../../store/AuthContext";
import { COLORS } from "../../theme";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [creds, setCreds] = useState({ login: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async () => {
    if (!creds.login.trim() || !creds.password) {
      setError("Please enter both login and password.");
      return;
    }
    setLoading(true); setError("");
    try {
      // Public call — no auth header needed for sign-in
      const data = await gqlPublic(ADMIN_SIGN_IN, { login: creds.login, password: creds.password });
      const res = data.adminSignIn;
      localStorage.setItem("token", res.token);
      login(res.token, { lastlogin: res.lastlogin });
      navigate("/businesses");
    } catch (e) {
      setError(e.message || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: COLORS.surface,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Left accent strip */}
      <Box sx={{
        position: "fixed", left: 0, top: 0, bottom: 0, width: 4,
        background: `linear-gradient(180deg, ${COLORS.accent} 0%, ${COLORS.accentMid} 100%)`,
      }} />

      <Paper sx={{
        width: 440, p: 5, borderRadius: 3,
        border: `1px solid ${COLORS.border}`, boxShadow: "0 4px 32px rgba(79,70,229,.08)",
      }}>
        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentMid} 100%)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 12px ${COLORS.accent}40`,
          }}>
            <Business sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.1 }}>Bixjet Admin</Typography>
            <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 500 }}>Plan Management Console</Typography>
          </Box>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Sign in</Typography>
        <Typography variant="body2" sx={{ color: COLORS.muted, mb: 3 }}>
          Access your admin dashboard to manage business plans.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError("")}>{error}</Alert>
        )}

        <TextField
          fullWidth
          label="Email / Username"
          value={creds.login}
          onChange={e => setCreds(p => ({ ...p, login: e.target.value }))}
          sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          inputProps={{ autoComplete: "off" }}
          autoFocus
        />
        <TextField
          fullWidth
          label="Password"
          type={showPw ? "text" : "password"}
          value={creds.password}
          onChange={e => setCreds(p => ({ ...p, password: e.target.value }))}
          onKeyDown={e => e.key === "Enter" && handleLogin()}
          sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          inputProps={{ autoComplete: "new-password" }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPw(p => !p)} edge="end" size="small">
                  {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          fullWidth variant="contained"
          onClick={handleLogin} disabled={loading}
          sx={{
            py: 1.4, borderRadius: 2, fontWeight: 700, fontSize: 15,
            bgcolor: COLORS.accent, "&:hover": { bgcolor: "#4338ca" },
            boxShadow: `0 4px 16px ${COLORS.accent}40`,
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Sign In"}
        </Button>

        <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 3, color: COLORS.textSub }}>
          Bixjet Admin © {new Date().getFullYear()}
        </Typography>
      </Paper>
    </Box>
  );
}
